import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Box, CircularProgress } from "@mui/material";

import Button from "../../../../Subcomponents/Button";
import ConfirmationAlert from "../../../../Subcomponents/ConfirmationAlert";
import InfoIcon from "../../../Icons/InfoIcon";
import { errorTost, successTost } from "../../../../Services/Toast";
import { resendFeedbackLink, canResendFeedback } from "../../../../Services/Feedback";
import {
  dash,
  fmtRating,
  maskEmail,
  maskMobile,
  statusPalette,
} from "../../../Feedback/feedbackFormat";

// Customer feedback for one allotment, rendered inline inside the View CRM
// Details modal rather than in a modal of its own — the surrounding view is
// already a modal, and the other tabs show their data inline.
//
// An allotment can hold MORE THAN ONE feedback record: feedback is per stage, so
// the same allotmentPaymentId can carry both PARKING_UPGRADE and AGREEMENT_DONE.
// Each record is one horizontal card, stacked newest-first as returned.
//
// The parent owns the fetch, because whether this tab appears at all depends on
// the result.

// Palette taken from the app's existing chips and neutrals — no new colours.
const BORDER = "#EAECF0";
const TITLE = "#101828";
const MUTED = "#667085";
const BLUE = "#155EEF";

const cardSx = {
  background: "#FFFFFF",
  border: `1px solid ${BORDER}`,
  borderRadius: "1rem",
  padding: { xs: "1.6rem", sm: "2rem 2.4rem" },
  transition: "box-shadow 0.15s ease, border-color 0.15s ease",
  "&:hover": {
    borderColor: "#D0D5DD",
    boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.06), 0px 1px 3px rgba(16, 24, 40, 0.10)",
  },
};

const Chip = ({ palette }) => (
  <Box
    component={"span"}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "0.6rem",
      padding: "0.3rem 1.2rem",
      borderRadius: "1.6rem",
      fontSize: "1.3rem",
      fontFamily: "Inter-Medium",
      backgroundColor: palette.bg,
      color: palette.color,
      border: `1px solid ${palette.bg}`,
      whiteSpace: "nowrap",
    }}
  >
    <Box
      component={"span"}
      sx={{
        width: "0.7rem",
        height: "0.7rem",
        borderRadius: "50%",
        backgroundColor: palette.dot,
        flexShrink: 0,
      }}
    />
    {palette.label}
  </Box>
);

// Separator between the meta items on the card's header line.
const Dot = () => (
  <Box
    component={"span"}
    sx={{
      width: "0.4rem",
      height: "0.4rem",
      borderRadius: "50%",
      background: "#D0D5DD",
      flexShrink: 0,
      display: { xs: "none", sm: "inline-block" },
    }}
  />
);

// "Label / value" pair used inside the expanded area.
const Field = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Box sx={{ fontSize: "1.3rem", color: MUTED, fontFamily: "Inter-Medium", mb: "0.2rem" }}>
      {label}
    </Box>
    <Box sx={{ fontSize: "1.5rem", color: TITLE, wordBreak: "break-word" }}>{children}</Box>
  </Box>
);

// Buttons sit on a card row, so they run compact rather than dominating the
// card. Kept as constants so the pair stays in step.
const BTN_TEXT = { fontSize: "1.2rem", lineHeight: "1.6rem", width: "auto" };
const BTN_PADDING = "0.4rem 1.1rem";

// Button sizes itself to its label instead of a fixed width — a fixed one was too narrow and broke
// "View Details" onto two lines. Button only forwards specific style keys, so whiteSpace has to be
// applied to the inner <p> from the wrapper.
const BTN_WRAP_SX = { "& p": { whiteSpace: "nowrap" } };

function AllotmentFeedbackTab({ records = [], loading, error, onResent }) {
  // Keyed by feedback_id — one allotment can show several cards, so a single
  // boolean would spin every button or expand every card at once.
  const [resendingId, setResendingId] = useState(null);
  const [confirmRecord, setConfirmRecord] = useState(null);
  const [expandedIds, setExpandedIds] = useState([]);

  // Named on the timeline entry the backend writes for the resend.
  const loggedInUser = useSelector((state) => state.Auth.user);

  const toggleExpanded = (id) =>
    setExpandedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const doResend = async (record) => {
    setResendingId(record.feedback_id);
    try {
      await resendFeedbackLink(record, loggedInUser?.name);
      successTost("Feedback link re-sent to the customer");
      onResent?.();
    } catch (e) {
      const status = e?.response?.status;
      errorTost(
        status === 404
          ? "No feedback request found to re-send for this record"
          : e?.response?.data?.error ||
              "Could not re-send the feedback link. Please try again."
      );
    } finally {
      setResendingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: "4rem" }}>
        <CircularProgress size={28} sx={{ color: BLUE }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ color: "#B42318", fontSize: "1.6rem", fontFamily: "Inter-Medium" }}>{error}</Box>
    );
  }

  if (records.length === 0) {
    return (
      <Box sx={{ fontSize: "1.6rem", color: MUTED }}>
        No feedback has been sent for this allotment.
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "1.6rem" }}>
        {records.map((record, index) => {
          // PENDING only. A SUBMITTED or CANCELLED record is rejected by the
          // feedback service with a 409, so the action would always fail.
          const showResend = record.status === "PENDING";
          const resendable = canResendFeedback(record);
          const busy = resendingId === record.feedback_id;
          const expanded = expandedIds.includes(record.feedback_id);

          return (
            <Box key={record.feedback_id ?? index} sx={cardSx}>
              {/* Header line: identity on the left, actions on the right. Wraps
                  to two rows on narrow screens rather than overflowing. */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "1.6rem",
                  flexWrap: "wrap",
                }}
              >
                <Box sx={{ flex: "1 1 26rem", minWidth: 0 }}>
                  <Box
                    sx={{
                      fontSize: "1.8rem",
                      fontFamily: "Inter-Medium",
                      fontWeight: 600,
                      color: TITLE,
                      mb: "0.8rem",
                      wordBreak: "break-word",
                    }}
                  >
                    {dash(record.customer_name)}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      flexWrap: "wrap",
                      fontSize: "1.4rem",
                      color: MUTED,
                    }}
                  >
                    <Box component={"span"}>{dash(record.stage_name)}</Box>
                    <Dot />
                    <Chip palette={statusPalette(record.status)} />
                    <Dot />
                    <Box component={"span"}>
                      Rating:{" "}
                      <Box component={"span"} sx={{ color: TITLE, fontFamily: "Inter-Medium" }}>
                        {fmtRating(record.rating)}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Actions: View Details, then Resend on the far right. */}
                <Box sx={{ display: "flex", gap: "0.8rem", flexShrink: 0, flexWrap: "wrap" }}>
                  <Box sx={BTN_WRAP_SX}>
                    <Button
                      style={{
                        color: BLUE,
                        backgroundColor: "#FFFFFF",
                        border: `1px solid ${BLUE}`,
                        ...BTN_TEXT,
                      }}
                      btnName={expanded ? "Hide Details" : "View Details"}
                      padding={BTN_PADDING}
                      clicked={() => toggleExpanded(record.feedback_id)}
                    />
                  </Box>

                  {showResend && (
                    <Box sx={BTN_WRAP_SX}>
                      <Button
                        style={{
                          color: "#FFFFFF",
                          backgroundColor: BLUE,
                          border: `1px solid ${BLUE}`,
                          ...BTN_TEXT,
                        }}
                        /* Shortened from "Resend Feedback Link" — the card is
                           already in the Feedback tab, so the context is clear
                           and the button can stay compact. */
                        btnName={busy ? "Sending…" : "Resend Link"}
                        padding={BTN_PADDING}
                        disable={busy || !resendable}
                        /* isLoading is deliberately not used: Button renders a
                           fixed 2.5rem spinner with a 2rem margin, which would
                           overflow a button this size. The "Sending…" label plus
                           the disabled state already cover the in-flight case. */
                        clicked={() => resendable && setConfirmRecord(record)}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              {/* The feedback service identifies a request by stage_code +
                  source_reference_id; without both, a resend can only 404, so
                  say why rather than leave a dead-looking disabled button. */}
              {showResend && !resendable && (
                <Box sx={{ mt: "1rem", fontSize: "1.3rem", color: MUTED }}>
                  This record cannot be re-sent — it is missing the stage or source
                  reference the feedback service needs.
                </Box>
              )}

              {/* Contact details only — the two fields needed to reach the
                  customer about this feedback. */}
              {expanded && (
                <Box sx={{ mt: "1.6rem", pt: "1.6rem", borderTop: `1px solid ${BORDER}` }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: "1.6rem 2.4rem",
                    }}
                  >
                    <Field label="Email">{dash(maskEmail(record.email))}</Field>
                    <Field label="Mobile">{dash(maskMobile(record.mobile))}</Field>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Resending puts a real WhatsApp message and email in front of the
          customer and cannot be undone, so it is confirmed first — the same
          ConfirmationAlert the rest of the app uses for irreversible actions. */}
      {confirmRecord && (
        <ConfirmationAlert
          open={Boolean(confirmRecord)}
          close={() => setConfirmRecord(null)}
          icon={<InfoIcon width={"4.8rem"} height={"4.8rem"} />}
          title={"Resend feedback link?"}
          subTitle={
            `The feedback form link will be sent again to ${dash(confirmRecord.customer_name)}` +
            `${confirmRecord.mobile ? ` on ${confirmRecord.mobile}` : ""}` +
            `${confirmRecord.email ? ` and ${confirmRecord.email}` : ""}.`
          }
          buttonTitle={"Cancel"}
          returnRespButton={"Resend Link"}
          disableConfirm={Boolean(resendingId)}
          confirm={(res) => {
            const record = confirmRecord;
            setConfirmRecord(null);
            if (res === "confirmed") doResend(record);
          }}
        />
      )}
    </>
  );
}

export default AllotmentFeedbackTab;
